import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import API_URL from '../api';
import ResumeScore from '../components/ResumeScore';
import MessageStudentModal from '../components/MessageStudentModal';
import logoMark from '../assets/images/logo.jpg';

function daysAgo(dateStr) {
  if (!dateStr) return null;
  const diff = Date.now() - new Date(dateStr).getTime();
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  if (days === 0) return 'today';
  if (days === 1) return '1 day ago';
  return `${days} days ago`;
}

function StudentProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [student, setStudent] = useState(null);
  const [loading, setLoading] = useState(true);
  // 'auth' (not signed in), 'forbidden', or 'notfound' — kept apart so the page
  // can explain itself instead of collapsing every failure into "not found".
  const [failure, setFailure] = useState(null);
  const [messageOpen, setMessageOpen] = useState(false);

  const token = localStorage.getItem('token');
  const viewer = (() => {
    try { return JSON.parse(localStorage.getItem('user') || 'null'); }
    catch { return null; }
  })();
  const isStaff = viewer && (viewer.role === 'recruiter' || viewer.role === 'admin');
  const isRecruiter = viewer?.role === 'recruiter';

  useEffect(() => { fetchStudent(); }, [id]);

  const fetchStudent = async () => {
    // GET /api/students/:id is auth-gated (it serves contact details and the
    // resume URL). Without a token the request is guaranteed to 401, so short-
    // circuit into the sign-in state rather than firing it and letting the
    // global interceptor decide what happens to the page.
    if (!token) {
      setFailure('auth');
      setLoading(false);
      return;
    }
    try {
      const response = await axios.get(`${API_URL}/api/students/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setStudent(response.data);
      setFailure(null);
    } catch (err) {
      const status = err?.response?.status;
      setFailure(status === 401 ? 'auth' : status === 403 ? 'forbidden' : 'notfound');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="sp-page">
        <div className="loading"><div className="spinner" /> Loading profile…</div>
      </div>
    );
  }

  if (!student) {
    const message = failure === 'auth'
      ? 'Please sign in to view this student profile.'
      : failure === 'forbidden'
        ? 'Only recruiters can view other students’ profiles.'
        : 'Student profile not found or not yet verified.';

    return (
      <div className="sp-page">
        <div style={{ maxWidth: 500, margin: '80px auto', padding: '0 24px', textAlign: 'center' }}>
          <div className="alert alert-error">{message}</div>
          {failure === 'auth'
            ? <button className="btn" onClick={() => navigate('/login')} style={{ marginTop: 16 }}>Sign In</button>
            : <button className="btn btn-secondary" onClick={() => navigate(-1)} style={{ marginTop: 16 }}>Go Back</button>}
        </div>
      </div>
    );
  }

  const college = student.college || student.iit_campus || '';
  const initial = (student.name || 'S').charAt(0).toUpperCase();
  const noticePeriod = student.notice_period || '';
  const workModes = Array.isArray(student.work_mode) ? student.work_mode : [];
  const hasRate = student.monthly_rate_min || student.monthly_rate_max;

  return (
    <div className="sp-page">
      {/* Nav */}
      <nav className="sp-nav">
        <span className="sp-nav-brand"><img src={logoMark} alt="" className="brand-mark" />HYRSKILL</span>
        <button className="btn btn-secondary btn-sm" onClick={() => navigate(-1)}>
          ← Back to Search
        </button>
      </nav>

      <div className="sp-body">
        <div className="sp-layout">
          {/* Sidebar */}
          <div className="sp-sidebar">
            {/* Avatar + name */}
            <div className="sp-avatar-card">
              <div className="sp-avatar">{initial}</div>
              <div className="sp-name">{student.name}</div>
              <div className="sp-college">{college}</div>
              <div className="sp-meta">Year {student.year} · {student.department}</div>
              <div className="sp-meta">{student.email}</div>

              {(noticePeriod || workModes.length > 0) && (
                <div className="sp-avail-tags">
                  {noticePeriod && <span className="sp-avail-tag">Joins in: {noticePeriod}</span>}
                  {workModes.map(m => <span key={m} className="sp-avail-tag">{m}</span>)}
                </div>
              )}

              {/* Recruiters only: messaging threads are Student↔Recruiter, so
                  there is no admin side of this conversation to open. */}
              {isRecruiter && (
                <button
                  className="btn btn-sm btn-full"
                  onClick={() => setMessageOpen(true)}
                  style={{ marginTop: 14 }}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
                  Message Student
                </button>
              )}
            </div>

            {/* Rate */}
            {hasRate && (
              <div className="sp-rate-card">
                <div className="sp-rate-label">Expected Monthly Stipend</div>
                <div className="sp-rate-value">
                  {student.monthly_rate_min ? `₹${(student.monthly_rate_min / 1000).toFixed(0)}k` : '?'}
                  {' – '}
                  {student.monthly_rate_max ? `₹${(student.monthly_rate_max / 1000).toFixed(0)}k` : '?'}
                </div>
                {student.weekly_commitment && (
                  <div style={{ fontSize: 12, color: 'var(--accent-dark)', marginTop: 6 }}>
                    {student.weekly_commitment} hrs/week
                  </div>
                )}
              </div>
            )}

            {/* Links */}
            {(student.resume_url || student.linkedin_url || student.github_url || student.portfolio_url) && (
              <div className="sp-links-card">
                {student.resume_url && (
                  <a href={`${API_URL}${student.resume_url.replace('/uploads/', '/api/uploads/')}`} target="_blank" rel="noopener noreferrer" className="sp-link">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                    Resume / CV
                  </a>
                )}
                {student.linkedin_url && (
                  <a href={student.linkedin_url} target="_blank" rel="noopener noreferrer" className="sp-link">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z"/><rect x="2" y="9" width="4" height="12"/><circle cx="4" cy="4" r="2"/></svg>
                    LinkedIn
                  </a>
                )}
                {student.github_url && (
                  <a href={student.github_url} target="_blank" rel="noopener noreferrer" className="sp-link">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 19c-5 1.5-5-2.5-7-3m14 6v-3.87a3.37 3.37 0 0 0-.94-2.61c3.14-.35 6.44-1.54 6.44-7A5.44 5.44 0 0 0 20 4.77 5.07 5.07 0 0 0 19.91 1S18.73.65 16 2.48a13.38 13.38 0 0 0-7 0C6.27.65 5.09 1 5.09 1A5.07 5.07 0 0 0 5 4.77a5.44 5.44 0 0 0-1.5 3.78c0 5.42 3.3 6.61 6.44 7A3.37 3.37 0 0 0 9 18.13V22"/></svg>
                    GitHub
                  </a>
                )}
                {student.portfolio_url && (
                  <a href={student.portfolio_url} target="_blank" rel="noopener noreferrer" className="sp-link">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>
                    Portfolio
                  </a>
                )}
              </div>
            )}
          </div>

          {/* Main content */}
          <div className="sp-main">
            {student.bio && (
              <div className="sp-section">
                <h3>About</h3>
                <p className="sp-bio">{student.bio}</p>
              </div>
            )}

            {student.skills && student.skills.length > 0 && (
              <div className="sp-section">
                <h3>Skills</h3>
                <div className="sp-skills-grid">
                  {student.skills.map(skill => (
                    <span key={skill._id} className="skill-tag">{skill.name}</span>
                  ))}
                </div>
              </div>
            )}

            {(noticePeriod || student.weekly_commitment || workModes.length > 0) && (
              <div className="sp-section">
                <h3>Availability</h3>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
                  {noticePeriod && (
                    <span className="sp-avail-tag">
                      Joins in: {noticePeriod}
                      {student.notice_period_updated_at && (
                        <span style={{ fontSize: 11, opacity: 0.6, marginLeft: 5 }}>
                          (set {daysAgo(student.notice_period_updated_at)})
                        </span>
                      )}
                    </span>
                  )}
                  {student.weekly_commitment && <span className="sp-avail-tag">{student.weekly_commitment} hrs / week</span>}
                  {workModes.map(m => <span key={m} className="sp-avail-tag">{m}</span>)}
                </div>
              </div>
            )}

            {isStaff && <ResumeScore studentId={id} />}

          </div>
        </div>
      </div>

      <MessageStudentModal
        open={messageOpen}
        student={{ id, name: student.name, subtitle: [college, student.department].filter(Boolean).join(' · ') }}
        onClose={() => setMessageOpen(false)}
      />
    </div>
  );
}

export default StudentProfile;
